import { useEffect, useRef, useState } from "react"
import { css, cx } from "@emotion/css"
import { useCombobox } from "downshift"
import { useI18n } from "providers/I18n"

import { useQuery, useMutation, queryCache } from "react-query"
import {
  patchUnassignedClients,
  fetchUnassignedClients,
} from "api/clientgroups"
import throttle from "lodash/throttle"

import Checkbox from "@material-ui/core/Checkbox"
import Chip from "@material-ui/core/Chip"
import CircularProgress from "@material-ui/core/CircularProgress"
import Collapse from "@material-ui/core/Collapse"
import FormControl from "@material-ui/core/FormControl"
import IconButton from "@material-ui/core/IconButton"
import InputLabel from "@material-ui/core/InputLabel"
import InputAdornment from "@material-ui/core/InputAdornment"
import OutlinedInput from "@material-ui/core/OutlinedInput"

import ExpandMoreIcon from "@material-ui/icons/ExpandMore"

import Button from "ui/buttons/Button"
import * as DG from "ui/dialogs"

const styles = {
  content: css`
    min-width: 580px;
    padding-top: 20px;
  `,

  inputWrapper: css`
    max-width: 400px;
    margin-right: auto;
  `,

  listWrapper: css`
    display: block;
    position: fixed;
    padding: 0.75rem 0;

    min-height: 48px;
    max-height: calc(8 * 38px + 1.5rem);
    overflow-y: auto;
    overflow-x: hidden;

    z-index: 42;
    background-color: #fff;
    box-shadow:
      0 2px 1px 0 rgba(0, 0, 0, 0.03),
      0 0 9px 0 rgba(0, 0, 0, 0.25);

    border-radius: 4px;
  `,
  list: css`
    margin: 0;
    padding: 0;
    position: relative;
    list-style: none;
  `,
  item: css`
    width: 100%;
    display: flex;
    padding: 0rem 1rem;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    text-align: left;
    text-decoration: none;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    cursor: pointer;
  `,
  itemDisabled: css`
    cursor: default;
    opacity: 0.4;
    pointer-events: none;
  `,
  itemHighlighted: css`
    background-color: rgba(0, 0, 0, 0.04);
  `,
  itemStart: css`
    min-width: 42px;
    flex-shrink: 0;
  `,
  itemText: css`
    flex: 1 1 auto;
    min-width: 0;
    position: relative;
  `,
  text: css`
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  code: css`
    min-width: 3.5rem;
    min-width: 10ch;
  `,
  name: css`
    color: var(--dark-grey);
    margin-left: 0.1rem;
    margin-left: 1ch;
  `,
  icon: css`
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &.expanded {
      transform: rotate(180deg);
    }
  `,

  chipsContainer: css`
    padding: 1rem 0;
    min-height: 80px;
  `,
  align: css`
    justify-self: start;
  `,
  between: css`
    justify-content: space-between;
  `,
}

interface ClientOption {
  value: string
  label: string
  code?: string
}

const query_key = "clients-input"
function fetchOptions(_: string, name: string): Promise<ClientOption[]> {
  const ps = new URLSearchParams([["limit", "100"]])
  if (name) {
    ps.set("name", name.trim())
  }
  return fetchUnassignedClients(query_key, ps.toString()).then((res) => {
    return res.data.map((x) => ({ label: x.name, value: x.id, code: x.code }))
  })
}

const fetchOptionsDebounced = throttle(fetchOptions, 200)

export type AddClientModalProps = {
  open: boolean
  onClose: () => void
  existingClientIds: Array<string>
  clientId: string
}

function AddClientModal(props: AddClientModalProps) {
  const { existingClientIds, clientId, open, onClose } = props
  const { t } = useI18n()

  const anchorRef = useRef<HTMLDivElement | null>(null)
  const {
    width: w = 0,
    height: h = 0,
    left: x = 0,
    top: y = 0,
  } = anchorRef.current?.getBoundingClientRect() ?? {}

  const [inputValue, setInputValue] = useState<string>(() => "")
  const [selectedItems, setSelectedItems] = useState<ClientOption[]>([])
  const handleClear = () => setSelectedItems([])

  const q = useQuery([query_key, inputValue], fetchOptionsDebounced)
  const options = q.data ?? []
  //eslint-disable-next-line
  const loading = q.status === "loading"

  const [addClients, { status: mustatus }] = useMutation(
    patchUnassignedClients,
    {
      onSuccess: () => {
        queryCache.invalidateQueries("clients-list")
        onClose()
      },
      throwOnError: true,
    },
  )

  const submitting = mustatus === "loading"

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
  } = useCombobox({
    id: "add-client",
    selectedItem: null,
    items: options,
    onInputValueChange: ({ inputValue: v }) =>
      v !== undefined && setInputValue(v),
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return
      }
      const index = selectedItems.findIndex(
        (x) => x.value === selectedItem.value,
      )
      if (index > 0) {
        setSelectedItems([
          ...selectedItems.slice(0, index),
          ...selectedItems.slice(index + 1),
        ])
      } else if (index === 0) {
        setSelectedItems([...selectedItems.slice(1)])
      } else {
        setSelectedItems([...selectedItems, selectedItem])
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          const disabled = existingClientIds.includes(
            changes.selectedItem?.value ?? "",
          )
          if (disabled) {
            changes.selectedItem = null
          }
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: "", // don't add the item string as input value at selection.
          }
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: "", // don't add the item string as input value at selection.
          }
        default:
          return changes
      }
    },
  })

  const handleChipDelete = (item: ClientOption) => {
    setSelectedItems((xs) => xs.filter((x) => x.value !== item.value))
  }

  const handleSubmit = () => {
    //
    addClients({
      id: clientId,
      clientIds: selectedItems.map((x) => x.value),
    })
  }

  const placeholderText = selectedItems.length
    ? `${t("clients.associate.filled")}: ${selectedItems.length} `
    : `${t("clients.assign")}`

  const id = "add-new-client-modal"

  useEffect(() => {
    if (!open) {
      setInputValue("")
      setSelectedItems([])
    }
  }, [open])

  return (
    <DG.Dialog
      open={open}
      onClose={onClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}> {t("clients.create.title")}</DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent className={styles.content}>
        <div ref={anchorRef} className={styles.inputWrapper}>
          <FormControl
            variant="outlined"
            fullWidth
            {...getComboboxProps({}, { suppressRefError: true })}
          >
            <InputLabel {...getLabelProps()} shrink>
              {t("client-groups.list.clients")}
            </InputLabel>
            <OutlinedInput
              notched
              {...getInputProps(
                {
                  refKey: "inputRef",
                  onFocus: () => {
                    if (!isOpen) {
                      openMenu()
                    }
                  },
                },
                { suppressRefError: true },
              )}
              placeholder={placeholderText}
              label={t("client-groups.list.clients")}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton {...getToggleButtonProps()} size="small">
                    <ExpandMoreIcon
                      className={cx(styles.icon, {
                        expanded: isOpen,
                      })}
                    />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </div>
        <Collapse in={isOpen}>
          <div
            className={styles.listWrapper}
            style={{
              top: `${y + h + 4}px`,
              left: `${x}px`,
              width: `${w}px`,
            }}
          >
            <ul
              {...getMenuProps({}, { suppressRefError: true })}
              className={styles.list}
            >
              {options.map((option, index) => {
                const disabled = existingClientIds.includes(option.value)
                return (
                  <li
                    className={cx(styles.item, {
                      [styles.itemHighlighted]: index === highlightedIndex,
                      [styles.itemDisabled]: disabled,
                    })}
                    key={`${option.value}-${index}`}
                    {...getItemProps({
                      item: option,
                      index,
                      disabled,
                    })}
                  >
                    <div className={styles.itemStart}>
                      <Checkbox
                        edge="start"
                        checked={
                          selectedItems.find(
                            (x) => x.value === option.value,
                          ) !== undefined
                        }
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                        size="small"
                      />
                    </div>
                    <div className={styles.itemText}>
                      <span className={cx(styles.text, styles.code)}>
                        {option.code}
                      </span>
                      <span className={cx(styles.text, styles.name)}>
                        {option.label}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </Collapse>
        <div className={styles.chipsContainer}>
          {selectedItems.map((item) => (
            <Chip
              color="primary"
              key={item.code}
              label={item.label}
              onDelete={() => handleChipDelete(item)}
            />
          ))}
        </div>
      </DG.DialogContent>

      <DG.DialogFooter
        className={cx({ [styles.between]: !!selectedItems?.length })}
      >
        {selectedItems.length ? (
          <Button
            onClick={handleClear}
            color="primary"
            disabled={submitting || selectedItems.length === 0}
            className={styles.align}
          >
            {t("clients.button.clear")}
          </Button>
        ) : null}
        <DG.DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            {t("clients.button.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting || selectedItems.length === 0}
            startIcon={
              submitting && <CircularProgress size={20} color="inherit" />
            }
          >
            {t("clients.button.submit")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default AddClientModal
